import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog} from '../Dialog'
import {FloatingField} from '../dialogs/AssignInvitationCodeFields.tsx'
import {DialogHeader} from '../dialogs/AssignInvitationCodeSteps.tsx'
import {ACTIVATION_CONSENT_TEXT_VERSION, ActivationView, consentChannelsFor, UpdateActivationTravelerPayload} from '../../domain/entities/Activation.ts'
import {useActivationPolicy} from '../../hooks/useActivations.ts'
import {activationPolicyMarket, fallbackActivationPolicy, isActivationContactValid, preferredChannelFor} from './activationPolicy.ts'
import {activationErrorMessage} from './activationErrors.ts'
import {ActivationContactFields} from './ActivationContactFields.tsx'
import IconSpinner from '../../assets/icons/spinner.svg?react'

interface EditActivationContactDialogProps {
    activation: ActivationView | null
    isSaving: boolean
    onSave: (payload: UpdateActivationTravelerPayload) => Promise<unknown>
}

// "Añadir o corregir contacto" / "Confirmar consentimiento" on a row with an
// activation: PATCH /activations/{id}/traveler. The backend recalculates the
// route, clears contactFailed when the failed channel changes and replans.
// Legacy rows (no activation) keep using EditInvitationCodeDialog.
export const EditActivationContactDialog = forwardRef<React.ElementRef<typeof Dialog<boolean>>, EditActivationContactDialogProps>(
    ({activation, isSaving, onSave}, ref) => {
        const {t} = useTranslation('dashboard')
        const dialog = useRef<React.ElementRef<typeof Dialog<boolean>>>(null)
        const [name, setName] = useState('')
        const [email, setEmail] = useState('')
        const [phone, setPhone] = useState('')
        const [noContact, setNoContact] = useState(false)
        const [consentGranted, setConsentGranted] = useState(false)
        const [error, setError] = useState<string | null>(null)

        // The activation's own market, which is what the backend validates against.
        const market = activationPolicyMarket(activation?.market)
        const {policy: fetchedPolicy} = useActivationPolicy(market, !!activation)
        const policy = fetchedPolicy ?? fallbackActivationPolicy(market)

        useEffect(() => {
            const traveler = activation?.traveler
            setName([traveler?.firstName, traveler?.lastName].filter(Boolean).join(' '))
            setEmail(traveler?.email ?? '')
            setPhone(traveler?.phone ?? '')
            setNoContact(!!traveler?.noContact)
            setConsentGranted(!!traveler?.consent?.granted)
            setError(null)
        }, [activation])

        useImperativeHandle(ref, () => ({
            open: () => dialog.current!.open(),
            close: dialog.current!.close,
        }), [])

        const close = () => dialog.current!.close()
        const isValid = isActivationContactValid(policy, noContact, email, phone)
        // A bounced channel the agency hasn't touched yet would just bounce again.
        const failedUnchanged = !!activation?.flags?.contactFailed && !noContact
            && email.trim() === (activation.traveler.email ?? '') && phone.trim() === (activation.traveler.phone ?? '')

        const submit = () => {
            if (!activation || !isValid || isSaving) return
            setError(null)
            const [firstName, ...rest] = name.trim().split(/\s+/).filter(Boolean)
            const granted = !noContact && consentGranted
            onSave({
                firstName: firstName || null,
                lastName: rest.join(' ') || null,
                email: email.trim() || null,
                phone: phone.trim() || null,
                preferredChannel: noContact ? null : preferredChannelFor(email, phone) ?? null,
                noContact,
                consent: {granted, textVersion: ACTIVATION_CONSENT_TEXT_VERSION, channels: granted ? consentChannelsFor(email, phone) : []},
            }).then(close).catch((e) => setError(activationErrorMessage(e, t)))
        }

        return (
            <Dialog ref={dialog} customWidth="520px" childrenPadding="p-0"
                    panelClassName="rounded-[22px] backdrop:bg-white/60 backdrop:backdrop-blur-md">
                <div className="pb-[18px]">
                    <DialogHeader title={t('activation.editContact.title', {code: activation?.code ?? ''})} onClose={close}/>
                </div>
                <form className="px-[26px] pb-2 flex flex-col gap-3.5" onSubmit={(e) => {e.preventDefault(); submit()}}>
                    <FloatingField label={t('dialogs.travelerNameLabel')} value={name} onChange={(e) => setName(e.target.value)}/>
                    <div className="flex gap-3 flex-wrap">
                        <div className="flex-1 basis-[180px] min-w-0">
                            <FloatingField label={t('dialogs.travelerPhoneLabel') + (!noContact && policy.requiredContact === 'PHONE' ? ' *' : '')}
                                           type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}/>
                        </div>
                        <div className="flex-1 basis-[180px] min-w-0">
                            <FloatingField label={t('dialogs.travelerEmailLabel') + (!noContact && policy.requiredContact === 'EMAIL' ? ' *' : '')}
                                           type="email" value={email} onChange={(e) => setEmail(e.target.value)}/>
                        </div>
                    </div>
                    {failedUnchanged && (
                        <p className="text-xs leading-[1.5] text-[#B3261E]">{t('activation.editContact.failedHint')}</p>
                    )}
                    <ActivationContactFields
                        policy={policy}
                        noContact={noContact}
                        onNoContactChange={setNoContact}
                        consentGranted={consentGranted}
                        onConsentChange={setConsentGranted}
                        error={error}
                    />
                    {/* Enter submits from any field. */}
                    <button type="submit" className="sr-only" tabIndex={-1} aria-hidden="true"/>
                </form>
                <div className="mt-4 flex items-center justify-end gap-3 flex-wrap border-t border-neutralBlack-100 bg-[#FCFCFB] px-[26px] py-4 rounded-b-[22px]">
                    <button type="button" onClick={close}
                            className="h-11 px-[18px] rounded-full border-[1.5px] border-neutralBlack-300 text-sm font-semibold text-neutralBlack-900 hover:border-neutralBlack-600">
                        {t('cancel')}
                    </button>
                    <button type="button" onClick={submit} disabled={!isValid || isSaving}
                            className="h-11 px-[18px] rounded-full bg-neutralBlack-900 text-white text-sm font-semibold hover:bg-neutralBlack-700 disabled:bg-neutralBlack-100 disabled:text-neutralBlack-500 disabled:cursor-not-allowed">
                        {isSaving ? <IconSpinner className="size-4"/> : t('activation.editContact.save')}
                    </button>
                </div>
            </Dialog>
        )
    }
)
